import { useCallback, useMemo } from "react";
import type { RoomView } from "@grundbesitz/shared";
import { TOTAL_HOTELS, TOTAL_HOUSES } from "@grundbesitz/shared";
import { debtView, groupStatus, lockNotice, ownedGroups, raiseSuggestions } from "../../lib/assets";
import type { AssetAction, RaiseOption } from "../../lib/assets";
import { formatMoney } from "../../lib/format";
import { HotelIcon, HouseIcon } from "../board/BuildingIcons";
import { BuildWindowNotice } from "./assets/BuildWindowNotice";
import { DebtBanner } from "./assets/DebtBanner";
import { GroupTag } from "./assets/GroupTag";
import { PropertyRow } from "./assets/PropertyRow";
import { useAct } from "./assets/useAct";

const TONE_CLASS = { ok: "text-ok", warn: "text-warn", info: "text-ink-2" } as const;

/** Manage own property: build, sell, mortgage. Legality and reasons come from the engine. */
export function AssetsPanel({ room }: { room: RoomView }) {
  const game = room.game;
  const pid = room.you.playerId;
  const me = game && pid ? game.players.find((p) => p.id === pid) : undefined;
  const { busy, run } = useAct();

  const groups = useMemo(() => (game && pid ? ownedGroups(game, pid) : []), [game, pid]);
  const debt = game && pid ? debtView(game, pid) : null;
  const suggestions = game && pid && debt ? raiseSuggestions(game, pid) : [];
  const lock = game && pid ? lockNotice(game, pid) : null;

  const onAction = useCallback((a: AssetAction, field: number) => void run(`${a.kind}-${field}`, a.action), [run]);
  const onRaise = useCallback((o: RaiseOption) => void run(`${o.kind}-${o.field}`, o.action), [run]);
  const onBankrupt = useCallback(() => void run("BANKRUPT", { type: "DECLARE_BANKRUPTCY" }), [run]);

  if (!game || !pid || !me || room.you.spectator) {
    return (
      <p className="rounded-xl border border-dashed border-line p-4 text-sm text-ink-2">
        Du schaust zu. Besitz verwalten können nur Spieler, die noch im Spiel sind.
      </p>
    );
  }
  if (me.eliminated) {
    return (
      <p className="rounded-xl border border-dashed border-line p-4 text-sm text-ink-2">
        Du bist ausgeschieden und schaust jetzt zu. Dein Besitz ging an den Gläubiger oder zurück an die Bank.
      </p>
    );
  }

  const debtMode = debt !== null;
  const hideHint = (message: string) => message === lock || (debtMode && message.startsWith("Mit Schulden"));
  const houseLow = game.bank.houses <= 4;
  const hotelLow = game.bank.hotels <= 2;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl bg-surface-2 px-3.5 py-2.5 text-sm">
        <span className="font-semibold">Bank-Vorrat</span>
        <span
          className={`inline-flex items-center gap-1.5 ${houseLow ? "font-semibold text-warn" : ""}`}
          aria-label={`${game.bank.houses} von ${TOTAL_HOUSES} Häusern in der Bank`}
        >
          <HouseIcon size={18} />
          <span className="tabular-nums">{game.bank.houses}</span> {game.bank.houses === 1 ? "Haus" : "Häuser"}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 ${hotelLow ? "font-semibold text-warn" : ""}`}
          aria-label={`${game.bank.hotels} von ${TOTAL_HOTELS} Hotels in der Bank`}
        >
          <HotelIcon size={20} />
          <span className="tabular-nums">{game.bank.hotels}</span> {game.bank.hotels === 1 ? "Hotel" : "Hotels"}
        </span>
        <span className="ml-auto text-ink-2">
          Bargeld <strong className="tabular-nums text-ink">{formatMoney(me.cash)}</strong>
        </span>
      </div>

      {debt && (
        <DebtBanner
          game={game}
          members={room.members}
          debt={debt}
          options={suggestions}
          busy={busy}
          onRun={onRaise}
          onBankrupt={onBankrupt}
        />
      )}

      <BuildWindowNotice room={room} />

      {lock && (
        <p role="status" className="rounded-xl border border-warn/70 px-3 py-2 text-sm">
          <strong>Gerade gesperrt.</strong> {lock}
        </p>
      )}

      {me.jailCards.length > 0 && (
        <section aria-label="Freikarten" className="rounded-xl border border-line bg-surface p-3 text-sm">
          <h3 className="font-display text-base font-semibold">
            {me.jailCards.length === 1 ? "1 Freikarte" : `${me.jailCards.length} Freikarten`}
          </h3>
          <p className="mt-0.5 text-ink-2">
            {me.inJail
              ? "Du sitzt im Gefängnis. Setze die Karte in der Aktionsleiste ein, wenn du am Zug bist."
              : "Du kommst damit aus dem Gefängnis frei. Einsetzen kannst du sie nur im Gefängnis, du kannst sie aber auch im Handel anbieten."}
          </p>
        </section>
      )}

      {groups.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line p-4 text-sm text-ink-2">
          Du besitzt noch keine Grundstücke. Kaufe eines, wenn du darauf landest, oder biete in einer Versteigerung mit.
        </p>
      ) : (
        groups.map((g) => {
          const status = groupStatus(game, pid, g);
          const headingId = `group-${g.meta.key}`;
          return (
            <section key={g.meta.key} aria-labelledby={headingId} className="space-y-2">
              <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <h3 id={headingId} className="text-sm">
                  <GroupTag group={g.meta} />
                </h3>
                <span className={`text-xs font-semibold ${TONE_CLASS[status.tone]}`}>
                  {status.tone === "ok" && <span aria-hidden="true">✓ </span>}
                  {status.headline}
                </span>
              </header>
              {status.detail && <p className="text-xs text-ink-2">{status.detail}</p>}
              <ul className="space-y-2">
                {g.owned.map((field) => (
                  <PropertyRow
                    key={field}
                    game={game}
                    pid={pid}
                    field={field}
                    group={g.meta}
                    debtMode={debtMode}
                    hideHint={hideHint}
                    busy={busy}
                    onRun={onAction}
                  />
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
